import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useOverlayFocusReturn } from './use-overlay-focus-return';
import { hasPrimitiveDescription } from './utils';

/**
 * Sober DS (v2) — Drawer (side sheet). Radix Dialog under the hood. The panel
 * slides in from the right (default) or left, sits on a 45%-opacity scrim, and
 * is built from a fixed surface with raised shadow. Header/body/footer mirror
 * the showcase .drawer__head/__body/__foot spec. Callers compose the Tabs
 * primitive inside DrawerBody for internal tabs. Single outline only — no
 * colored borders or accent bars.
 */
export const Drawer = DialogPrimitive.Root;
export const DrawerTrigger = DialogPrimitive.Trigger;
export const DrawerClose = DialogPrimitive.Close;
export const DrawerPortal = DialogPrimitive.Portal;

export const DrawerOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    style={{ backgroundColor: 'rgba(28,29,33,0.45)' }}
    className={cn(
      'fixed inset-0 z-50',
      'so-overlay-motion data-[state=open]:animate-in data-[state=closed]:animate-out duration-200 ease-out motion-reduce:animate-none',
      'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
      className
    )}
    {...props}
  />
));
DrawerOverlay.displayName = 'DrawerOverlay';

export interface DrawerContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  side?: 'right' | 'left';
}

export const DrawerContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DrawerContentProps
>(({ className, children, side = 'right', ...props }, ref) => {
  const focusReturn = useOverlayFocusReturn(props);
  const hasDescription = hasPrimitiveDescription(children, 'DrawerDescription');
  // Mesmo piso do Dialog: sem título o Radix avisa em runtime e o leitor de tela não
  // consegue anunciar o painel. Hoje todos os painéis têm o seu; isto impede o próximo
  // de nascer sem.
  const hasTitle = hasPrimitiveDescription(children, 'DrawerTitle');

  return (
    <DrawerPortal>
      <DrawerOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed inset-y-0 z-50 flex h-dvh-safe w-[440px] max-w-[92vw] flex-col overflow-hidden',
          'bg-sober-surface shadow-so-raised',
          'so-overlay-motion data-[state=open]:animate-in data-[state=closed]:animate-out duration-200 ease-out motion-reduce:animate-none',
          'focus:outline-none',
          side === 'right'
            ? 'right-0 data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right'
            : 'left-0 data-[state=open]:slide-in-from-left data-[state=closed]:slide-out-to-left',
          className
        )}
        {...props}
        {...focusReturn}
      >
        {!hasTitle && (
          <DialogPrimitive.Title className="sr-only">Painel lateral</DialogPrimitive.Title>
        )}
        {!hasDescription && (
          <DialogPrimitive.Description className="sr-only">
            Painel lateral do ValiosasCRM.
          </DialogPrimitive.Description>
        )}
        {children}
        {!hasPrimitiveDescription(children, 'DrawerCloseButton') && (
          <DrawerCloseButton className="absolute right-3 top-3" />
        )}
      </DialogPrimitive.Content>
    </DrawerPortal>
  );
});
DrawerContent.displayName = 'DrawerContent';

export const DrawerHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'relative flex shrink-0 flex-col items-start gap-1 border-b border-sober-outline bg-sober-surface/95 px-6 pb-4 pr-16 pt-5 backdrop-blur',
        className
      )}
      {...props}
    />
  )
);
DrawerHeader.displayName = 'DrawerHeader';

export const DrawerTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-so-16 font-bold text-sober-text', className)}
    {...props}
  />
));
DrawerTitle.displayName = 'DrawerTitle';

export const DrawerDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-so-14 text-sober-secondary', className)}
    {...props}
  />
));
DrawerDescription.displayName = 'DrawerDescription';

export const DrawerBody = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-6 [scrollbar-gutter:stable]', className)}
      {...props}
    />
  )
);
DrawerBody.displayName = 'DrawerBody';

export const DrawerFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex max-w-full shrink-0 flex-wrap justify-end gap-2 border-t border-sober-outline bg-sober-surface/95 p-4 px-6 backdrop-blur',
        className
      )}
      {...props}
    />
  )
);
DrawerFooter.displayName = 'DrawerFooter';

export const DrawerCloseButton = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Close
    ref={ref}
    className={cn(
      'grid h-11 w-11 shrink-0 place-items-center rounded-so-md text-sober-secondary',
      'transition-colors hover:bg-sober-muted hover:text-sober-text',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sober-primary-fg',
      className
    )}
    aria-label="Fechar"
    {...props}
  >
    {children ?? <X className="size-4" />}
  </DialogPrimitive.Close>
));
DrawerCloseButton.displayName = 'DrawerCloseButton';
